import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import {
  X, PaperPlaneTilt, Users, Spinner, CheckCircle, Warning, EnvelopeSimple,
} from '@phosphor-icons/react';

/**
 * BulkEmailPreviewDrawer — opens when Crunchy's `draft_bulk_email` tool fires.
 *
 * The user can:
 *   • Edit the subject + body (live merge-field tokens like {first_name})
 *   • Pick the send provider (Gmail vs. SalesCrunch system / Resend)
 *   • Hit "Send to N contacts" — queues POST /api/email/send-bulk-adhoc as a
 *     background job and polls live progress (no more 120s proxy timeouts)
 *   • Hit "Stop sending" mid-run — cancels the remaining emails
 */
export default function BulkEmailPreviewDrawer({ open, onClose, preview }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [provider, setProvider] = useState('resend');
  const [providerInfo, setProviderInfo] = useState({ sender: '', gmail_email: '', gmail_connected: false });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [connectingGmail, setConnectingGmail] = useState(false);
  const [justConnected, setJustConnected] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const pollRef = useRef(null);
  const jobPollRef = useRef(null);

  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(jobPollRef.current); }, []);

  const connectGmail = async () => {
    if (connectingGmail) return;
    setConnectingGmail(true);
    try {
      const { data } = await api.get(`/oauth/gmail/start?frontend_origin=${encodeURIComponent(window.location.origin)}`);
      if (!data.auth_url) throw new Error('No auth URL');
      window.open(data.auth_url, 'gmail-oauth', 'width=540,height=680');
      // Poll until the popup completes OAuth (it closes itself on success)
      clearInterval(pollRef.current);
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        if (tries > 60) {
          clearInterval(pollRef.current);
          setConnectingGmail(false);
          return;
        }
        try {
          const { data: st } = await api.get('/email/status');
          if (st.gmail_connected) {
            clearInterval(pollRef.current);
            setProviderInfo(st);
            setProvider('gmail');
            setConnectingGmail(false);
            setJustConnected(true);
            setResult(null);
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setConnectingGmail(false);
      setResult({ error: err.response?.data?.detail || 'Could not start Gmail connect.' });
    }
  };

  // When a fresh preview arrives, hydrate the fields
  useEffect(() => {
    if (!open || !preview) return;
    setSubject(preview.subject_template || '');
    setBody(preview.body_template || '');
    setResult(null);
    setSending(false);
    setStopRequested(false);
    clearInterval(jobPollRef.current);
  }, [open, preview]);

  // Load sender info (which email is configured + is Gmail connected)
  useEffect(() => {
    if (!open) return;
    api.get('/email/status').then((r) => setProviderInfo(r.data || {})).catch(() => {});
  }, [open]);

  const recipientCount = preview?.contact_count || 0;
  const sample = preview?.sample_contacts || [];

  const pollJob = (jobId) => {
    clearInterval(jobPollRef.current);
    jobPollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/email/bulk-jobs/${jobId}`);
        setResult(data);
        if (data.status === 'done' || data.status === 'error' || data.status === 'cancelled') {
          clearInterval(jobPollRef.current);
          setSending(false);
          if (data.status === 'done' && data.failed_count === 0) {
            setTimeout(() => onClose?.(), 3000);
          }
        }
      } catch {
        clearInterval(jobPollRef.current);
        setSending(false);
      }
    }, 2000);
  };

  const stopSending = async () => {
    if (!result?.job_id || stopRequested) return;
    setStopRequested(true);
    try {
      await api.post(`/email/bulk-jobs/${result.job_id}/cancel`);
    } catch { /* the poll will reflect the final state either way */ }
  };

  const handleSend = async () => {
    if (sending || !preview) return;
    if (!subject.trim() || !body.trim()) {
      setResult({ error: 'Subject and body are required.' });
      return;
    }
    setSending(true);
    setResult(null);
    setStopRequested(false);
    try {
      const { data } = await api.post('/email/send-bulk-adhoc', {
        contact_ids: preview.all_contact_ids,
        subject,
        body,
        provider,
      });
      setResult(data); // {job_id, status: "queued", total, ...}
      pollJob(data.job_id);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || err.message || 'Send failed' });
      setSending(false);
    }
  };

  if (!open) return null;

  const jobRunning = result && !result.error && (result.status === 'queued' || result.status === 'running');
  const jobDone = result && !result.error && result.status === 'done';
  const jobCancelled = result && result.status === 'cancelled';
  const jobErrored = result && result.status === 'error';
  const progressDone = (result?.sent_count || 0) + (result?.failed_count || 0);
  const progressPct = result?.total ? Math.round((progressDone / result.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end" data-testid="bulk-email-preview-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={sending ? undefined : onClose} />

      {/* Drawer panel */}
      <div className="relative w-full max-w-[540px] h-full bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 bg-gradient-to-b from-[#0B5394] to-[#073763] text-white flex items-center gap-2">
          <EnvelopeSimple size={18} weight="fill" />
          <span className="text-[13px] font-bold tracking-wide uppercase">Review & Approve Bulk Email</span>
          <div className="flex-1" />
          <button
            onClick={onClose}
            disabled={sending}
            className="text-white/70 hover:text-white disabled:opacity-40"
            aria-label="Close"
            data-testid="bulk-preview-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-[#FAFAF8]">
          {/* Audience */}
          <div className="bg-white border border-[#DDD] rounded-sm p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Users size={15} className="text-[#0B5394]" weight="bold" />
              <span className="text-[12px] font-semibold text-[#333]">Recipients</span>
              <span className="text-[11px] text-[#666] bg-[#EEF4FB] border border-[#B8D0E8] px-2 py-0.5 rounded-sm" data-testid="bulk-recipient-count">
                {recipientCount.toLocaleString()} {recipientCount === 1 ? 'contact' : 'contacts'}
              </span>
              {preview?.audience_label && (
                <span className="text-[11px] text-[#666]">· {preview.audience_label}</span>
              )}
            </div>
            {sample.length > 0 && (
              <p className="text-[11px] text-[#555] leading-relaxed">
                {sample.join(', ')}
                {recipientCount > sample.length && (
                  <span className="text-[#888]"> and {(recipientCount - sample.length).toLocaleString()} more</span>
                )}
              </p>
            )}
            {recipientCount === 0 && (
              <p className="text-[11px] text-[#C0392B]">No matching contacts — try a wider filter.</p>
            )}
          </div>

          {/* Provider */}
          <div>
            <label className="text-[11px] font-semibold text-[#333] uppercase tracking-wide block mb-1">Send via</label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
              data-testid="bulk-provider-select"
              disabled={sending}
            >
              <option value="resend">SalesCrunch system ({providerInfo.sender || 'system'})</option>
              <option value="gmail" disabled={!providerInfo.gmail_connected}>
                {providerInfo.gmail_connected
                  ? `My Gmail (${providerInfo.gmail_email})`
                  : 'My Gmail — not connected (Settings → Email)'}
              </option>
            </select>
            {provider === 'resend' && (
              providerInfo.resend_daily_cap == null ? (
                <p className="text-[10px] text-[#888] mt-1">
                  Recipients see your verified company email as the sender.
                </p>
              ) : (
                <p className="text-[10px] text-[#888] mt-1" data-testid="resend-quota-note">
                  Shared SalesCrunch domain — capped at {providerInfo.resend_daily_cap} emails/day
                  ({providerInfo.resend_remaining_today} left today).
                  {!providerInfo.gmail_connected && (
                    <> For bigger sends, connect your Gmail in <b>Settings → Email</b>.</>
                  )}
                </p>
              )
            )}
            {provider === 'resend' && providerInfo.resend_remaining_today != null
              && recipientCount > providerInfo.resend_remaining_today && (
              <div className="mt-1" data-testid="resend-quota-warning">
                <p className="text-[10px] text-[#B45309] font-medium">
                  ⚠ This batch ({recipientCount.toLocaleString()}) exceeds your remaining shared-domain sends.
                  {providerInfo.gmail_connected && ' Switch "Send via" to your Gmail above.'}
                </p>
                {!providerInfo.gmail_connected && (
                  <button
                    onClick={connectGmail}
                    disabled={connectingGmail || sending}
                    data-testid="drawer-connect-gmail-btn"
                    className="mt-1.5 px-3 py-1 text-[11px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 rounded-sm"
                  >
                    {connectingGmail ? 'Waiting for Google…' : 'Connect Gmail now →'}
                  </button>
                )}
              </div>
            )}
            {provider === 'gmail' && (
              <p className="text-[10px] text-[#888] mt-1">
                Sends from your personal Gmail (500/day limit). Replies land in your Gmail inbox.
              </p>
            )}
            {justConnected && (
              <p className="text-[10px] text-emerald-700 font-medium mt-1" data-testid="gmail-just-connected-note">
                ✓ Gmail connected ({providerInfo.gmail_email}) — sender switched to your Gmail.
              </p>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="text-[11px] font-semibold text-[#333] uppercase tracking-wide block mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full h-[32px] px-2 text-[13px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
              data-testid="bulk-subject-input"
              disabled={sending}
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-[11px] font-semibold text-[#333] uppercase tracking-wide">Body</label>
              <span className="text-[10px] text-[#888]">
                Merge tokens: <code className="bg-[#EEF4FB] px-1">{'{first_name}'}</code> <code className="bg-[#EEF4FB] px-1">{'{last_name}'}</code> <code className="bg-[#EEF4FB] px-1">{'{company}'}</code>
              </span>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full px-2 py-1.5 text-[13px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394] font-mono resize-none"
              data-testid="bulk-body-input"
              disabled={sending}
            />
          </div>

          {/* Live progress while the background job runs */}
          {jobRunning && (
            <div className="p-3 rounded-sm border bg-blue-50 border-blue-200 text-blue-900 text-[12px]" data-testid="bulk-send-progress">
              <div className="flex items-center gap-2 mb-1.5">
                <Spinner size={14} className="animate-spin" weight="bold" />
                <b>Sending… {progressDone.toLocaleString()} of {(result.total || 0).toLocaleString()}</b>
              </div>
              <div className="h-[8px] bg-blue-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#0B5394] transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-[10px] text-blue-800">
                  Each send is logged to the contact&apos;s History.
                </p>
                <button
                  onClick={stopSending}
                  disabled={stopRequested}
                  data-testid="bulk-stop-send-btn"
                  className="px-3 py-1 text-[11px] font-bold bg-[#C0392B] text-white hover:bg-[#96281B] disabled:opacity-60 rounded-sm shrink-0"
                >
                  {stopRequested ? 'Stopping…' : '■ Stop sending'}
                </button>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (result.error || jobDone || jobCancelled || jobErrored) && (
            <div
              className={`p-3 rounded-sm border text-[12px] ${
                result.error || jobErrored
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : (jobCancelled || result.failed_count > 0)
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-900'
              }`}
              data-testid="bulk-send-result"
            >
              {result.error || jobErrored ? (
                <div>
                  <div className="flex items-start gap-2"><Warning size={14} weight="bold" /> {result.error}</div>
                  {(result.error || '').includes('shared SalesCrunch domain') && !providerInfo.gmail_connected && (
                    <button
                      onClick={connectGmail}
                      disabled={connectingGmail}
                      data-testid="error-connect-gmail-btn"
                      className="mt-2 px-3 py-1 text-[11px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 rounded-sm"
                    >
                      {connectingGmail ? 'Waiting for Google…' : 'Connect Gmail now →'}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" />
                  <div>
                    {jobCancelled && <b>Stopped early — </b>}
                    Sent to <b>{result.sent_count}</b> contact{result.sent_count === 1 ? '' : 's'}
                    {jobCancelled && <> of {(result.total || 0).toLocaleString()} — the rest were cancelled</>}.
                    {result.failed_count > 0 && (
                      <>
                        {' '}{result.failed_count} failed.
                        {(result.failed || []).length > 0 && (
                          <ul className="mt-1 text-[11px] list-disc pl-4">
                            {result.failed.slice(0, 5).map((f, i) => (
                              <li key={i}>{f.email || f.contact_id}: {f.reason}</li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-[#F5F5F0] border-t border-[#DDD] flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-[12px] font-medium border border-[#999] bg-white hover:bg-[#F0F0F0]"
            data-testid="bulk-cancel-btn"
          >
            {jobRunning ? 'Close (keeps sending)' : 'Cancel'}
          </button>
          <div className="flex-1" />
          <button
            onClick={handleSend}
            disabled={sending || recipientCount === 0 || jobDone || jobCancelled}
            className="px-4 py-1.5 text-[12px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 flex items-center gap-1.5"
            data-testid="bulk-send-btn"
          >
            {sending ? (
              <><Spinner size={13} className="animate-spin" weight="bold" /> Sending…</>
            ) : (
              <><PaperPlaneTilt size={13} weight="bold" /> Send to {recipientCount.toLocaleString()} contacts</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
